import { Box, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Layout } from '@roc/client-portal-shared/components';
import {
  FinancialInformationStep,
  IdInformationStep,
} from '@roc/feature-borrowers';
import { VerticalBarLoader } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useStore } from '../../hooks';
import { IdVerificationLayout } from './components/idVerificationLayout';
import { ThankYou } from './components/thankYou';
import { IdVerificationLinkStatus } from './stores/identityCheckPublicStore';

const ID_INFORMATION_STEP = 0;
const FINANCIAL_INFORMATION_STEP = 1;

const LINK_MESSAGES = {
  [IdVerificationLinkStatus.NEW_LINK_SENT]:
    'This link is no longer valid. If it matches our records, we have emailed a new secure link. Please use the link in that email to continue.',
  [IdVerificationLinkStatus.INVALID]:
    'This link is invalid. Please contact us for a new link.',
};

export const IdVerification = observer(() => {
  // The URL segment is a signed token. Old links (plain base64 ids) are still accepted by the
  // backend only to email the borrower a new signed link; they never open the form.
  const { encodedParams } = useParams<{ encodedParams: string }>();
  const { identityCheckStore } = useStore();
  const [active, setActive] = useState(ID_INFORMATION_STEP);
  const { formInfo, linkStatus } = identityCheckStore;
  const title = "ID Verification";

  useEffect(() => {
    identityCheckStore.loadFromLink(encodedParams);
  }, [encodedParams]);

  const formComplete =
    formInfo?.status === 'FINISHED' ||
    (formInfo?.financialInformationComplete && formInfo?.driversLicenseUploaded);

  const onIdInformationNext = async () => {
    if (identityCheckStore.validateIdInformationStep()) {
      const saved = await identityCheckStore.uploadDriversLicenseFromLink();
      if (saved && identityCheckStore.formInfo?.driversLicenseUploaded) {
        setActive(FINANCIAL_INFORMATION_STEP);
      }
    }
  };

  const onFinancialInformationNext = async () => {
    if (identityCheckStore.validateFinancialInfomrationStep()) {
      await identityCheckStore.saveFinancialInformationFromLink();
    }
  };

  if (linkStatus === null) {
    return <VerticalBarLoader />;
  }

  if (linkStatus !== IdVerificationLinkStatus.VALID) {
    return (
      <Layout maxWidth="md">
         <Box textAlign={'left'}>
          <Typography variant={'h3'}>{title}</Typography>
        </Box>
        <Box mt={4}>
          <Alert severity="warning">{LINK_MESSAGES[linkStatus] ?? LINK_MESSAGES[IdVerificationLinkStatus.INVALID]}</Alert>
        </Box>
      </Layout>
    );
  }

  const steps = [
    {
      title: 'ID Information',
      icon: null,
      allowBack: false,
      allowNext: true,
      onNext: onIdInformationNext,
    },
    {
      title: 'Financial Information',
      icon: null,
      allowBack: true,
      allowNext: true,
      onBack: () => setActive(ID_INFORMATION_STEP),
      onNext: onFinancialInformationNext,
    },
  ];

  return (
    <IdVerificationLayout
      title={title}
      steps={steps}
      active={active}
      complete={formComplete}
      SuccessScreen={ThankYou}
    >
      <IdInformationStep displayPersonalInformation={true} />
      <FinancialInformationStep />
    </IdVerificationLayout>
  );
});
